import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ColumnNamePipe } from '../../../../pipes/column-name.pipe';
import { FilterModel } from '../../../../core/models/filters/filters.model';
import { ChatStatus } from '../../../../../enums/chat-status.enum';
import { ColumnModel } from '../../../../core/models/table/column.model';
import { SortModel } from '../../../../core/models/filters/sort.model';
import { PaginationComponent } from '../../pagination/pagination.component';
import { Router, RouterModule } from '@angular/router';
import { DeleteModalComponent } from '../../modals/delete-modal/delete-modal.component';
import { ActionButtonClickModel } from '../../../../core/models/action-button/action-button-click.model';
import { CaseStatus } from '../../../../../enums/case-status.enum';
import { StatusUtils } from '../../../../utils/status-utils';
import { FiltersComponent } from '../../filters/filters.component';
import { UserStatusEnum } from '../../../../../enums/user-status.enum';
declare var bootstrap: any;

export interface TableData {
  [key: string]: string | number | Date;
}

@Component({
  selector: 'app-tables',
  standalone: true,
  imports: [
    DatePipe,
    CommonModule,
    FormsModule,
    TranslateModule,
    PaginationComponent,
    RouterModule,
    DeleteModalComponent,
    FiltersComponent,
  ],
  templateUrl: './tables.component.html',
  styleUrl: './tables.component.scss',
  providers: [DatePipe, ColumnNamePipe],
})
export class TablesComponent implements OnInit {
  @Input() data: any[] = [];
  @Input() columns: ColumnModel[] = [];
  @Input() filters: FilterModel[] = [];
  @Input() tableHeader: string = '';
  @Input() footerText: string = '';
  @Input() tableLoading: boolean = false;
  @Input() columnWidths: string[] = [];
  @Input() totalCount: number = 0;
  @Input() totalPage: number = 0;
  @Input() pageSize: number = 0;
  @Input() iconPath: string;

  @Output() filtersChange = new EventEmitter<{
    filters: FilterModel[] | null;
    sort: SortModel | null;
  }>();
  @Output() onChangePage = new EventEmitter<{
    page: number;
    pageSize: number;
  }>();
  @Output() onPageChange = new EventEmitter<number>();
  @Output() onPageSizeChange = new EventEmitter<number>();
  @Output() onActionButtonClick = new EventEmitter<ActionButtonClickModel>();
  @Output() handleGetPatientList = new EventEmitter<void>();
  @Output() handleGetConsultationList = new EventEmitter<void>();
  @Output() handleGetUserList = new EventEmitter<void>();

  ChatStatus = ChatStatus;
  caseStatuses = StatusUtils?.getFilterCaseStatuses();
  caseStatus = CaseStatus;
  userStatus = UserStatusEnum;

  sortColumn: string | null = null;
  sortDirection: null | 'ascending' | 'descending' = null;
  pageSizes = [10, 25, 50, 100];
  currentPage: number = 1;
  deleteModalId: string = '';
  maxDate: string;
  filterForm = {
    customerCode: '',
    year: null as number | null,
    month: null as number | null,
    doctor: '',
    pageSize: 10,
  };
  currentYear = new Date().getFullYear();
  minYear = this.currentYear - 5;
  maxYear = this.currentYear + 5;
  listName: string = '';
  isPatientList: boolean = window.location.href.includes('patient');
  isConsultationList: boolean = window.location.href.includes('consultation');
  isUserList: boolean = window.location.href.includes('/user/list');
  patientReport: string = '';
  patientRecordId: string = '';
  patientRecordCaseId: string = '';
  pidNo: string = '';

  constructor(private translate: TranslateService, private router: Router) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
  }

  async ngOnInit() {
    await StatusUtils?.initializeTranslateService(this.translate);
    this.caseStatuses = StatusUtils?.getFilterCaseStatuses();
  }

  filterList(e: FilterModel[]) {
    this.filtersChange.emit({ filters: e, sort: null });
  }

  getTotalForLastColumn(): number {
    const lastColumnKey = this.columns[this.columns.length - 1];
    return this.data.reduce((total, row) => {
      const value = row[lastColumnKey.key];
      return total + (typeof value === 'number' ? value : 0);
    }, 0);
  }

  getStatusName(status: number): string {
    return StatusUtils?.getCaseStatusText(status);
  }

  getStatusClass(status: number): string {
    return StatusUtils?.getCaseStatusClass(status);
  }

  getUserStatusClass(status: string): string {
    const formatttedStatus = parseInt(status);
    return StatusUtils?.getUserStatusClass(formatttedStatus);
  }

  getUserStatusText(status: string): string {
    const formatttedStatus = parseInt(status);
    return StatusUtils?.getUserStatusText(formatttedStatus);
  }

  getChatStatusClass(status: number): string {
    return StatusUtils?.getChatStatusClass(status);
  }

  getChatStatusText(status: ChatStatus): string {
    return StatusUtils?.getChatStatusText(status);
  }

  sortTable(key: string) {
    if (this.sortColumn === key) {
      this.sortDirection =
        this.sortDirection === null
          ? 'ascending'
          : this.sortDirection === 'ascending'
          ? 'descending'
          : null;
    } else {
      this.sortColumn = key;
      this.sortDirection = 'ascending';
    }

    if (this.sortDirection === null) {
      this.sortColumn = null;
    }

    const sortModel: SortModel = { column: key, orderBy: this.sortDirection };
    this.filtersChange.emit({
      filters: this.filters,
      sort: sortModel ? sortModel : null,
    });
  }

  pageChange(page: any): void {
    this.currentPage = page;
    this.onPageChange.emit(page);
  }

  pageSizeChange(size: any): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.onPageSizeChange.emit(size);
  }

  handleRouting(row: any) {
    if (this.isPatientList) {
      this.router.navigate(['/patient/detail', row.patientId]);
    } else if (this.isConsultationList) {
      this.router.navigate(['/consultation/detail', row.patientRecordCaseId]);
    }
  }

  handleCursorPointer() {
    if (this.isConsultationList || this.isPatientList) {
      return 'cursor-pointer';
    }
    return '';
  }

  actionButtonClick(
    actionType: 'activate' | 'block' | 'edit' | 'delete',
    row: any
  ) {
    this.onActionButtonClick.emit({ actionType, row });
  }

  handleDelete(row: any, event: Event) {
    event.stopPropagation();
    if (this.isUserList) {
      this.listName = 'user-list';
      this.setDeleteModalId(row.id);
    }
  }

  setDeleteModalId(id: string) {
    this.deleteModalId = id;
    setTimeout(() => {
      const modalElement = document.getElementById('deleteModal-' + id);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    });
  }

  deleteEvent() {
    if (this.isPatientList) {
      this.handleGetPatientList.emit();
    } else if (this.isConsultationList) {
      this.handleGetConsultationList.emit();
    } else if (this.isUserList) {
      this.handleGetUserList.emit();
    }
  }
}
